import { useCallback, useEffect, useRef, useState } from "react";
import { StreamAudio } from "./StreamAudio";

const keyFor = (token) => `crank_play_${token.slice(0, 16)}`;
const STOP_CODES = new Set([4000, 4403, 4404]);

export function useCloudStream(token, api) {
  const [phase, setPhase] = useState("idle");
  const [info, setInfo] = useState({ queue: 0, reason: "", size: [1280, 720] });
  const [quality, setQuality] = useState({ fps: 0, rtt: 0 });
  const wsRef = useRef(null);
  const canvasRef = useRef(null);
  const keyRef = useRef(sessionStorage.getItem(keyFor(token)) || "");
  const retry = useRef(0);
  const frames = useRef(0);
  const closing = useRef(false);
  const audio = useRef(null);
  if (!audio.current) audio.current = new StreamAudio();
  const rttRef = useRef(0);

  const send = useCallback((m) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) ws.send(JSON.stringify(m));
  }, []);

  const draw = useCallback(async (buf) => {
    const c = canvasRef.current;
    if (!c) return;
    try {
      const bmp = await createImageBitmap(new Blob([buf], { type: "image/jpeg" }));
      if (c.width !== bmp.width || c.height !== bmp.height) { c.width = bmp.width; c.height = bmp.height; }
      c.getContext("2d").drawImage(bmp, 0, 0);
      bmp.close();
      frames.current += 1;
    } catch { /* skip bad frame */ }
  }, []);

  const connect = useCallback(() => {
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws");
    const ws = new WebSocket(`${base}/api/cloud/ws/${token}?k=${encodeURIComponent(keyRef.current)}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    ws.onopen = () => { retry.current = 0; };
    ws.onmessage = (e) => {
      if (typeof e.data !== "string") {
        const kind = new Uint8Array(e.data, 0, 1)[0];
        if (kind === 1) draw(e.data.slice(1));
        else if (kind === 2) audio.current.push(e.data.slice(1));
        return;
      }
      const m = JSON.parse(e.data);
      if (m.type === "hello") { setInfo((i) => ({ ...i, size: [m.w, m.h] })); setPhase(m.state === "running" ? "playing" : "starting"); }
      if (m.type === "pong") {
        rttRef.current = Math.round(performance.now() - m.ts);
        setQuality((q) => ({ ...q, rtt: rttRef.current, jq: m.jq }));
        if (m.state === "running") setPhase((p) => (p === "starting" || p === "reconnecting" ? "playing" : p));
      }
      if (m.type === "ended") { if (!closing.current) setInfo((i) => ({ ...i, reason: m.reason })); closing.current = true; setPhase("ended"); }
    };
    ws.onclose = (e) => {
      if (wsRef.current !== ws) return;
      wsRef.current = null;
      if (closing.current || STOP_CODES.has(e.code)) {
        if (!closing.current) { setInfo((i) => ({ ...i, reason: e.code === 4403 ? "This session belongs to another device." : "The session has ended." })); setPhase("ended"); }
        return;
      }
      if (retry.current >= 8) { setInfo((i) => ({ ...i, reason: "Lost connection to the game server." })); setPhase("ended"); return; }
      setPhase("reconnecting");
      const wait = Math.min(1000 * 2 ** retry.current, 10000);
      retry.current += 1;
      setTimeout(() => !closing.current && connect(), wait);
    };
  }, [token, draw]);

  const start = useCallback(async (q) => {
    audio.current.unlock();
    setPhase("starting");
    try {
      const { data } = await api.post(`/cloud/play/${token}/start`, { device_key: keyRef.current, quality: q });
      keyRef.current = data.device_key;
      sessionStorage.setItem(keyFor(token), data.device_key);
      for (let i = 0; i < 600 && !closing.current; i++) {
        const { data: st } = await api.get(`/cloud/play/${token}/status`, { params: { k: keyRef.current } });
        if (st.session_state === "queued") { setPhase("queued"); setInfo((x) => ({ ...x, queue: st.queue_position })); }
        else if (st.session_state === "running") break;
        else if (st.session_state === "ended") { setInfo((x) => ({ ...x, reason: st.error ? `${st.end_reason} (${st.error.split("\n")[0]})` : st.end_reason })); setPhase("ended"); return; }
        await new Promise((r) => setTimeout(r, 1500));
      }
      connect();
    } catch (e) {
      setInfo((x) => ({ ...x, reason: e.response?.data?.detail || "Couldn't start the game." }));
      setPhase("ended");
    }
  }, [api, token, connect]);

  const exit = useCallback(async () => {
    closing.current = true;
    setInfo((i) => ({ ...i, reason: "You left the game." }));
    setPhase("ended");
    try { await api.post(`/cloud/play/${token}/exit`, null, { params: { k: keyRef.current } }); } catch { /* already gone */ }
    wsRef.current?.close();
  }, [api, token]);

  useEffect(() => {
    const t = setInterval(() => {
      send({ t: "ping", ts: performance.now(), rtt: rttRef.current });
      setQuality((q) => ({ ...q, fps: frames.current }));
      frames.current = 0;
    }, 1000);
    return () => { clearInterval(t); closing.current = true; wsRef.current?.close(); audio.current.close(); };
  }, [send]);

  return { phase, info, quality, canvasRef, send, start, exit, hasKey: !!keyRef.current, audio: audio.current };
}
